import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { ref, onValue, update, get, increment } from 'firebase/database';
import { analyzeReport } from '../../services/aiService';
import { sendTemplateNotification } from '../../services/notificationService';
import { NOTIFICATION_TEMPLATES } from '../../data/notificationTemplates';
import './AdminReports.css';

function AdminReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [resolutionTemplate, setResolutionTemplate] = useState('ACTION_COMPLETED');
  const [proposedId, setProposedId] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const reportsRef = ref(db, 'reports');
    const unsubscribe = onValue(reportsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.values(data).map(report => ({
          ...report,
          aiScrutiny: analyzeReport(report)
        })).sort((a, b) => b.createdAt - a.createdAt);
        setReports(list);
      } else {
        setReports([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (selectedReport && selectedReport.category === 'Profile ID Migration') {
      setProposedId(selectedReport.requestedId || '');
    }
    setRejectReason('');
  }, [selectedReport]);

  const generateAIId = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let newId = "DEV-";
    for (let i = 0; i < 6; i++) {
        newId += chars[Math.floor(Math.random() * chars.length)];
    }
    setProposedId(newId);
  };

  const handleStatusUpdate = async (reportId, newStatus) => {
    try {
      setSending(true);
      await update(ref(db, `reports/${reportId}`), {
        status: newStatus,
        updatedAt: Date.now()
      });

      if (newStatus === 'resolved' && selectedReport) {
        await sendTemplateNotification(selectedReport.uid, resolutionTemplate);
        alert(`Report marked as resolved. Notification "${NOTIFICATION_TEMPLATES[resolutionTemplate].title}" sent to user.`);
      }

      setSelectedReport(prev => prev ? {...prev, status: newStatus} : null);
    } catch (err) {
      alert("Failed to update status");
    } finally {
      setSending(false);
    }
  };

  const handleApproveIdChange = async () => {
    if (!proposedId || !selectedReport) return;
    try {
      setSending(true);
      // 1. Get user to check current idChangesCount
      const userRef = ref(db, `users/${selectedReport.uid}`);
      const userSnap = await get(userRef);
      if (!userSnap.exists()) throw new Error("User not found");
      
      // 2. Atomic Update: ID, Count
      await update(userRef, {
        profileId: proposedId,
        idChangesCount: increment(1)
      });

      // 3. Mark report as resolved
      await update(ref(db, `reports/${selectedReport.id}`), {
        status: 'resolved',
        updatedAt: Date.now(),
        finalId: proposedId
      });

      // 4. Notify User
      await sendTemplateNotification(selectedReport.uid, 'ID_CHANGE_APPROVED', {
        newId: proposedId,
        caseId: selectedReport.reportId
      });

      alert(`ID Migration Approved. User profile updated to ${proposedId}.`);
      setSelectedReport(null);
    } catch (err) {
      alert("Failed to approve ID change: " + err.message);
    } finally {
      setSending(false);
    }
  };

  const handleRejectIdChange = async () => {
    if (!rejectReason || !selectedReport) {
      alert("Please provide a reason for rejection.");
      return;
    }
    try {
      setSending(true);
      
      // 1. Restore points if it was a paid change (idChangesCount > 0 previously)
      const userRef = ref(db, `users/${selectedReport.uid}`);
      const userSnap = await get(userRef);
      const userData = userSnap.val();

      if (userData.idChangesCount > 0) {
        await update(userRef, {
            points: increment(20)
        });
      }

      // 2. Mark report as closed
      await update(ref(db, `reports/${selectedReport.id}`), {
        status: 'rejected',
        updatedAt: Date.now(),
        rejectionReason: rejectReason
      });

      // 3. Notify User
      await sendTemplateNotification(selectedReport.uid, 'ID_CHANGE_REJECTED', {
        caseId: selectedReport.reportId,
        reason: rejectReason
      });

      alert("ID Migration Request Rejected. User notified.");
      setSelectedReport(null);
    } catch (err) {
      alert("Failed to reject ID change.");
    } finally {
      setSending(false);
    }
  };

  const handleCancelCase = async () => {
     if (!selectedReport) return;
     try {
        setSending(true);
        await update(ref(db, `reports/${selectedReport.id}`), {
            status: 'cancelled',
            updatedAt: Date.now()
        });
        await sendTemplateNotification(selectedReport.uid, 'ID_CHANGE_CANCELLED', {
            caseId: selectedReport.reportId
        });
        alert("Case cancelled and closed.");
        setSelectedReport(null);
     } catch (e) {
        alert("Failed to cancel case.");
     } finally {
        setSending(false);
     }
  };

  if (loading) return <div className="text-center" style={{padding: '50px', color: '#ffa116'}}>Loading Reports...</div>;

  return (
    <div className="animate-up">
      <div className="admin-page-header">
        <h1>User Reports & Appeals</h1>
        <p>Manage complaints, bug reports, and suspension appeals.</p>
      </div>

      <div style={{display: 'grid', gridTemplateColumns: selectedReport ? '1fr 400px' : '1fr', gap: '20px', alignItems: 'start'}}>
        
        {/* Reports List */}
        <div className="admin-card">
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Category</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr 
                    key={report.reportId} 
                    onClick={() => setSelectedReport(report)}
                    style={{cursor: 'pointer', background: selectedReport?.reportId === report.reportId ? 'rgba(0, 255, 174, 0.05)' : ''}}
                  >
                    <td style={{fontFamily: 'monospace', fontSize: '0.8rem'}}>{report.reportId}</td>
                    <td>{report.userName}</td>
                    <td style={{textTransform: 'capitalize'}}>{report.category.replace('_', ' ')}</td>
                    <td>{report.subject}</td>
                    <td>
                      <span className={`status-badge ${report.status === 'open' ? 'status-active' : 'status-banned'}`} 
                            style={report.status === 'open' ? {} : {background: 'rgba(255,255,255,0.05)', color: '#8b949e', border: '1px solid #30363d'}}>
                        {report.status.toUpperCase()}
                      </span>
                    </td>
                    <td>{new Date(report.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
                {reports.length === 0 && (
                  <tr>
                    <td colSpan="6" style={{textAlign: 'center', padding: '40px', color: '#8b949e'}}>No reports found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Report Details Sidebar */}
        {selectedReport && (
          <div className="report-details-sidebar animate-up" key={selectedReport.reportId}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <h3 style={{margin: 0, color: '#fff'}}>Report Details</h3>
              <button 
                onClick={() => setSelectedReport(null)}
                style={{background: 'none', border: 'none', color: '#8b949e', cursor: 'pointer'}}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            {selectedReport.aiScrutiny && (
              <div className="ai-scrutiny-box">
                <div className="ai-scrutiny-header">
                  <span className="ai-scrutiny-label">
                    <i className="fa-solid fa-robot"></i> AI SCRUTINY
                  </span>
                  <span className={`priority-badge priority-${selectedReport.priority || selectedReport.aiScrutiny.priority}`}>
                    {(selectedReport.priority || selectedReport.aiScrutiny.priority).toUpperCase()} PRIORITY
                  </span>
                </div>
                <p className="ai-scrutiny-summary">
                  {selectedReport.aiScrutiny.summary}
                </p>
                <div style={{fontSize: '0.7rem', color: 'var(--brand-accent)', opacity: 0.8}}>
                  Processed by Origin Core.
                </div>
              </div>
            )}

            {selectedReport.category === 'Profile ID Migration' && (
              <div className="report-technical-data" style={{background: 'rgba(0, 255, 174, 0.05)', padding: '15px', borderRadius: '8px', margin: '15px 0', border: '1px solid rgba(0, 255, 174, 0.1)'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', marginBottom: '10px', color: '#fff', borderBottom: '1px solid rgba(0,255,174,0.1)', paddingBottom: '5px'}}>
                  <span><i className="fa-solid fa-code-merge"></i> Technical Migration</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem'}}>
                  <span style={{color: '#8b949e'}}>Current:</span>
                  <code style={{color: 'var(--brand-accent)'}}>{selectedReport.currentId}</code>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginTop: '5px'}}>
                  <span style={{color: '#8b949e'}}>Requested:</span>
                  <code style={{color: '#fff', fontWeight: 'bold'}}>{selectedReport.requestedId}</code>
                </div>
                <div style={{textAlign: 'center', marginTop: '15px'}}>
                   <div style={{fontSize: '0.65rem', color: 'var(--brand-accent)', background: '#000', padding: '3px 10px', borderRadius: '4px', border: '1px solid var(--brand-accent)', display: 'inline-block'}}>
                      <i className="fa-solid fa-microscope"></i> PENDING TECHNICAL SCRUTINY
                   </div>
                </div>
              </div>
            )}

            <div className="report-field">
              <label className="report-field-label">From User</label>
              <div className="report-field-value">{selectedReport.userName || 'Unknown User'}</div>
              <div style={{fontSize: '0.85rem', color: '#8b949e'}}>{selectedReport.userEmail}</div>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '8px'}}>
                <div style={{fontSize: '0.7rem', color: 'var(--brand-accent)', background: 'rgba(0,255,174,0.05)', padding: '2px 8px', borderRadius: '4px', fontFamily: 'monospace', border: '1px solid rgba(0,255,174,0.1)'}}>SUID: {selectedReport.suid || 'N/A'}</div>
                <div style={{fontSize: '0.7rem', color: '#8b949e', background: 'rgba(255,255,255,0.03)', padding: '2px 8px', borderRadius: '4px', fontFamily: 'monospace', border: '1px solid rgba(255,255,255,0.05)'}}>UID: {selectedReport.uid}</div>
              </div>
            </div>

            <div className="report-field">
              <label className="report-field-label">Category</label>
              <div style={{color: '#ffa116', textTransform: 'capitalize', fontWeight: 'bold'}}>{selectedReport.category.replace('_', ' ')}</div>
            </div>

            <div className="report-field">
              <label className="report-field-label">Description</label>
              <div className="report-description-box">
                {selectedReport.details}
              </div>
            </div>

            <div className="resolution-box">
              <label className="report-field-label">Actions</label>
              
              {selectedReport.status === 'open' && selectedReport.category === 'Profile ID Migration' ? (
                <div className="id-migration-actions">
                    <div style={{marginBottom: '20px', background: 'rgba(255,255,255,0.02)', padding: '15px', borderRadius: '8px', border: '1px solid var(--border-color)'}}>
                        <label style={{display: 'block', fontSize: '0.7rem', color: '#8b949e', textTransform: 'uppercase', marginBottom: '10px'}}>Final Identifier Assignment</label>
                        <div style={{display: 'flex', gap: '10px'}}>
                            <input 
                                type="text" 
                                className="resolution-select" 
                                value={proposedId} 
                                onChange={(e) => setProposedId(e.target.value)}
                                style={{flex: 1, fontFamily: 'monospace', fontSize: '1.1rem'}}
                            />
                            <button className="btn-ai-suggest" onClick={generateAIId} title="Auto-Generate ID">
                                <i className="fa-solid fa-wand-magic-sparkles"></i>
                            </button>
                        </div>
                    </div>

                    <div style={{marginBottom: '20px'}}>
                        <label style={{display: 'block', fontSize: '0.7rem', color: '#8b949e', textTransform: 'uppercase', marginBottom: '8px'}}>Rejection Reason (If declining)</label>
                        <textarea 
                            className="resolution-select" 
                            style={{height: '60px', padding: '10px', fontSize: '0.9rem'}}
                            placeholder="e.g. Identifier violates platform naming standards."
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                        />
                    </div>

                    <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                        <button className="btn-resolve" onClick={handleApproveIdChange} disabled={sending} style={{background: 'var(--brand-accent)', color: '#000'}}>
                            <i className="fa-solid fa-check-double"></i> {sending ? 'Processing...' : 'Approve & Execute Swap'}
                        </button>
                        <div style={{display: 'flex', gap: '10px'}}>
                            <button className="btn-resolve" onClick={handleRejectIdChange} disabled={sending} style={{flex: 1, background: '#f85149'}}>
                                <i className="fa-solid fa-ban"></i> Reject
                            </button>
                            <button className="btn-resolve" onClick={handleCancelCase} disabled={sending} style={{flex: 1, background: '#8b949e'}}>
                                <i className="fa-solid fa-xmark"></i> Cancel Case
                            </button>
                        </div>
                    </div>
                </div>
              ) : selectedReport.status === 'open' ? (
                <div style={{width: '100%'}}>
                  <div style={{marginBottom: '15px'}}>
                    <label style={{display: 'block', fontSize: '0.7rem', color: '#8b949e', textTransform: 'uppercase', marginBottom: '8px'}}>Resolution Notification</label>
                    <select 
                      className="resolution-select" 
                      value={resolutionTemplate}
                      onChange={(e) => setResolutionTemplate(e.target.value)}
                    >
                      <optgroup label="Success">
                        {Object.keys(NOTIFICATION_TEMPLATES).filter(k => NOTIFICATION_TEMPLATES[k].type === 'success').map(k => (
                          <option key={k} value={k}>{NOTIFICATION_TEMPLATES[k].title}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Info">
                        {Object.keys(NOTIFICATION_TEMPLATES).filter(k => NOTIFICATION_TEMPLATES[k].type === 'info').map(k => (
                          <option key={k} value={k}>{NOTIFICATION_TEMPLATES[k].title}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Warning">
                        {Object.keys(NOTIFICATION_TEMPLATES).filter(k => NOTIFICATION_TEMPLATES[k].type === 'warning').map(k => (
                          <option key={k} value={k}>{NOTIFICATION_TEMPLATES[k].title}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                  <button 
                    className="btn-resolve" 
                    onClick={() => handleStatusUpdate(selectedReport.reportId, 'resolved')}
                    disabled={sending}
                  >
                    <i className="fa-solid fa-check-circle"></i> {sending ? 'Processing...' : 'Mark Resolved & Notify'}
                  </button>
                </div>
              ) : (
                <div className="resolution-complete">
                  <i className="fa-solid fa-circle-check"></i>
                  <span>Case {selectedReport.status?.toUpperCase()}</span>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default AdminReports;
